'use client';

import { FaqSection } from '@/components/sections/faq-section';
import { useTranslation } from '@/lib/i18n/context';

type FaqItem = {
  q: string;
  a: string;
};

const CONTENT = {
  en: {
    eyebrow: 'FAQ',
    heading: 'Plain-English answers.',
    subtitle:
      'No legalese here — just how the guarantee actually works in practice.',
    faq: [
      {
        q: 'When does the 120-day clock start?',
        a: "The day your paid Open subscription goes live. Trial conversations and pre-production sandboxing don't count toward the 120 days.",
      },
      {
        q: 'What counts as “not satisfied”?',
        a: "Anything. You don't need to justify the refund or hit a specific metric. If you tell us within the first 120 days that Open isn't working for you, we refund every dollar of Open spend — no questions asked.",
      },
      {
        q: 'Do I have to hit a resolution rate or any other metric?',
        a: "No. The Refund Guarantee is purely satisfaction-based. There's no automation rate to clear, no CSAT bar to beat, no numbers to argue about. If you're not happy, you're refunded.",
      },
      {
        q: 'What does the refund cover?',
        a: 'Every dollar of Open spend within the 120-day window — software, per-resolution charges, and any implementation or training fees billed by Open. Capped at $2 million per customer.',
      },
      {
        q: 'How fast does the refund land?',
        a: 'Within 30 days of your refund request. Wired as cash, no clawbacks, no “but actually” clauses.',
      },
      {
        q: "What if I'm not eligible at sign-up?",
        a: 'Reach out to your Open account team. We review eligibility case-by-case for customers transitioning off Early Stage or promotional plans, or for accounts with non-standard contract terms.',
      },
    ] as FaqItem[],
  },
  ar: {
    eyebrow: 'الأسئلة الشائعة',
    heading: 'إجابات واضحة، بدون لغة محامين.',
    subtitle: 'كيف يشتغل الضمان فعلاً على أرض الواقع.',
    faq: [
      {
        q: 'متى تبدأ الـ ١٢٠ يوم؟',
        a: 'من أول يوم يبدأ فيه اشتراكك المدفوع في Open. وما تُحتسب محادثات التجربة ولا الاختبار قبل الإطلاق ضمن الـ ١٢٠ يوم.',
      },
      {
        q: 'وش يعتبر «مو راضي»؟',
        a: 'أي شي. ما تحتاج تبرّر الاسترجاع، ولا توصل رقم معيّن. إذا خبّرتنا خلال أول ١٢٠ يوم إن Open ما يناسبك، نرجّع لك كل ريال دفعته عليه — بدون أي أسئلة.',
      },
      {
        q: 'لازم أوصل نسبة حل معيّنة أو أي مؤشر آخر؟',
        a: 'لا. ضمان الاسترجاع مبني على رضاك وحده. لا نسبة أتمتة لازم توصلها، ولا حدّ CSAT لازم تتجاوزه، ولا أرقام للجدال حولها. وإذا ما كنت راضي، نرجّع لك المبلغ.',
      },
      {
        q: 'وش يغطي الاسترجاع؟',
        a: 'كل ريال دفعته على Open خلال الـ ١٢٠ يوم — البرنامج، ورسوم كل حالة حل، وأي رسوم تجهيز أو تدريب فوترها Open. بحد أقصى ٧,٥ مليون ⃁ لكل عميل.',
      },
      {
        q: 'كم ياخذ الاسترجاع حتى يوصل؟',
        a: 'خلال ٣٠ يوم من طلب الاسترجاع. تحويل بنكي نقداً، بلا أي استرداد لاحق، وبلا بنود «بس في الحقيقة».',
      },
      {
        q: 'وش الحل إذا ما كنت مؤهّلاً عند التسجيل؟',
        a: 'تواصل مع فريق حسابك في Open. نراجع الأهلية حالة بحالة للعملاء المنتقلين من باقات Early Stage أو الباقات الترويجية، أو للحسابات اللي شروط عقدها مو اعتيادية.',
      },
    ] as FaqItem[],
  },
} as const;

export const GuaranteeFaq = () => {
  const { locale } = useTranslation();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;

  return (
    <FaqSection
      tone="dark"
      eyebrow={c.eyebrow}
      title={c.heading}
      subtitle={c.subtitle}
      items={c.faq.map((item) => ({ question: item.q, answer: item.a }))}
      className="border-t border-white/5"
    />
  );
};

export default GuaranteeFaq;
